import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, AlertTriangle, History } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, Field, Money } from "@/components/common";
import { CompanyStage, SessionBanner, focusTid } from "@/components/ClientSession";
import SalesSubmissions from "@/components/SalesSubmissions";
import { rangeFor } from "@/components/PeriodBar";
import { api, formatErr } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/format";
import DateInput from "@/components/DateInput";
import { useAuth } from "@/context/AuthContext";

export const salesVat = (total, rate) => {
  const t = Number(total) || 0;
  const vat = Math.round((t * rate / (100 + rate)) * 100) / 100;
  return { vat, net: Math.round((t - vat) * 100) / 100 };
};

const OverlapList = ({ rows }) => (
  <ul className="text-xs text-amber-900 num space-y-0.5 border border-amber-200 bg-amber-50 rounded p-3">
    {rows.map((d) => <li key={d.id}>{d.record_no} · {fmtDate(d.period_from)} – {fmtDate(d.period_to)} · {fmtMoney(d.total_sales)} SAR</li>)}
  </ul>
);

export default function SalesEntry() {
  const { settings } = useAuth();
  const nav = useNavigate();
  const rate = Number(settings?.standard_vat_rate ?? 15);
  const [m0, m1] = rangeFor("month");
  const [client, setClient] = useState(null);
  const [f, setF] = useState({ period_from: m0, period_to: m1, total_sales: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [overlap, setOverlap] = useState(null);
  const [saved, setSaved] = useState(null);
  const wrap = useRef(null);
  const { vat, net } = salesVat(f.total_sales, rate);
  const ready = client && f.period_from && f.period_to && f.period_from <= f.period_to && Number(f.total_sales) > 0;

  useEffect(() => { if (client) setTimeout(() => focusTid("sales-period-from-input"), 80); }, [client]);

  const quick = (p) => { const [a, b] = rangeFor(p); setF((x) => ({ ...x, period_from: a, period_to: b })); };

  const submit = async (confirm = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/sales", { client_id: client.id, ...f, total_sales: Number(f.total_sales), confirm_overlap: confirm });
      setSaved(data);
      setOverlap(null);
      toast.success(`Sales entry ${data.record_no} saved`);
      setF((x) => ({ ...x, total_sales: "", notes: "" }));
      setTimeout(() => focusTid("sales-period-from-input"), 80);
    } catch (e) {
      if (e.response?.status === 409 && e.response.data.detail?.duplicates) setOverlap(e.response.data.detail.duplicates);
      else toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  const onKeys = (e) => {
    if (e.key !== "Enter") return;
    if (e.ctrlKey || e.metaKey) { e.preventDefault(); if (ready && !busy) submit(false); return; }
    const t = e.target;
    if (t.tagName === "BUTTON" || !t.hasAttribute("data-nav")) return;
    e.preventDefault();
    const els = [...wrap.current.querySelectorAll("[data-nav]")].filter((el) => !el.disabled);
    els[els.indexOf(t) + 1]?.focus();
  };

  return (
    <div>
      <PageHeader eyebrow="Data entry" title="Sales entry" subtitle={`Record total sales (VAT inclusive) for a period. VAT at ${rate}% is extracted automatically from the entered amount.`}
        actions={<Button variant="outline" onClick={() => nav("/sales")} data-testid="sales-history-link"><History className="w-4 h-4 mr-1.5" />Sales history</Button>} />
      {!client ? <CompanyStage onPick={(c) => c && setClient(c)} noun="sales entry" /> : (<>
        <SessionBanner client={client} onChange={() => setClient(null)} />
        <SalesSubmissions clientId={client.id} rate={rate} onApproved={setSaved} />
        {saved && (
          <div className="mb-5 panel border-emerald-300 bg-emerald-50 px-4 py-3 flex flex-wrap items-center gap-3 fade-up" data-testid="sales-saved-confirmation">
            <CheckCircle2 className="w-5 h-5 text-emerald-700" />
            <div className="text-sm text-emerald-900"><span className="num font-semibold">{saved.record_no}</span> saved · {fmtDate(saved.period_from)} – {fmtDate(saved.period_to)} · <span className="num">{fmtMoney(saved.total_sales)} SAR</span> · VAT <span className="num">{fmtMoney(saved.vat_amount)}</span></div>
          </div>
        )}
        <div ref={wrap} onKeyDown={onKeys} className="grid xl:grid-cols-[1fr_380px] gap-6">
          <div className="panel p-5 sm:p-6 space-y-6 fade-up" data-testid="sales-entry-form">
            <section>
              <div className="eyebrow mb-3">1 · Sales period</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="From date" required><DateInput value={f.period_from} onChange={(v) => setF({ ...f, period_from: v })} className="h-10" data-nav testid="sales-period-from-input" /></Field>
                <Field label="To date" required error={f.period_from > f.period_to ? "To date must be on or after From date" : undefined}><DateInput value={f.period_to} onChange={(v) => setF({ ...f, period_to: v })} className="h-10" data-nav testid="sales-period-to-input" /></Field>
              </div>
              <div className="flex gap-2 mt-3">
                {[["month", "Current month"], ["last_month", "Last month"]].map(([p, l]) => <button key={p} type="button" onClick={() => quick(p)} className="text-xs px-2.5 py-1 rounded-full border border-slate-200 hover:border-emerald-400 text-slate-600" data-testid={`sales-quick-${p}`}>{l}</button>)}
              </div>
            </section>
            <section>
              <div className="eyebrow mb-3">2 · Sales amount (SAR)</div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Total sales (incl. VAT)" required><Input type="number" min="0" step="0.01" value={f.total_sales} onChange={(e) => setF({ ...f, total_sales: e.target.value })} className="h-10 num text-right" placeholder="0.00" data-nav data-testid="sales-total-input" /></Field>
                <Field label={`VAT amount (${rate}% included)`}><div className="h-10 px-3 flex items-center justify-end rounded-md bg-amber-50 border border-amber-200 num font-semibold text-amber-900" data-testid="sales-vat-display">{fmtMoney(vat)}</div></Field>
                <Field label="Net sales (excl. VAT)"><div className="h-10 px-3 flex items-center justify-end rounded-md bg-slate-50 border border-slate-200 num text-slate-800" data-testid="sales-net-display">{fmtMoney(net)}</div></Field>
              </div>
              <p className="text-[11px] text-slate-400 mt-2 num">VAT = Total × {rate} ÷ {100 + rate}</p>
            </section>
            <section>
              <Field label="Notes (optional)"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="h-10" placeholder="e.g. POS summary, branch" data-nav data-testid="sales-notes-input" /></Field>
            </section>
          </div>
          <aside>
            <div className="panel p-5 xl:sticky xl:top-20 fade-up">
              <div className="eyebrow mb-3">Entry summary</div>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Client</dt><dd className="truncate text-right">{client.company_name}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-500">Period</dt><dd className="num text-xs">{fmtDate(f.period_from)} – {fmtDate(f.period_to)}</dd></div>
              </dl>
              <div className="border-t border-slate-200 mt-4 pt-4 space-y-1.5">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Net sales</span><Money value={net} /></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">VAT ({rate}%)</span><Money value={vat} /></div>
                <div className="flex justify-between text-base pt-1"><span className="font-medium">Total sales</span><Money value={f.total_sales} strong /></div>
              </div>
              <Button className="w-full mt-5 h-11 bg-emerald-800 hover:bg-emerald-900" disabled={!ready || busy} onClick={() => submit(false)} data-nav data-testid="sales-entry-submit-button">{busy ? "Saving…" : "Save sales entry"}</Button>
            </div>
          </aside>
        </div>
      </>)}
      <Dialog open={!!overlap} onOpenChange={(o) => !o && setOverlap(null)}>
        <DialogContent className="bg-white" data-testid="sales-overlap-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" />Overlapping sales period</DialogTitle>
            <DialogDescription>An existing sales entry for this client already covers part of this period. Save anyway?</DialogDescription>
          </DialogHeader>
          {overlap && <OverlapList rows={overlap} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverlap(null)}>Review</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" disabled={busy} onClick={() => submit(true)} data-testid="sales-overlap-confirm-button">Save anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
